import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { FacetData } from '~/contracts';
import usePriceDrop from '~/hooks/usePriceDrop';
import { mergeFromBaseline, setPriceDropDisplayName } from './utils';

type FacetDataContextType = {
  baseline: Partial<FacetData>;
  didExpandMenu: boolean;
  facetData: LoadableState<Partial<FacetData>>;
  triggerMenuDidExpand: () => void;
};

const FacetDataContext = createContext<FacetDataContextType>({
  baseline: {},
  didExpandMenu: false,
  facetData: defaultLoadableState<Partial<FacetData>>({}),
  triggerMenuDidExpand() {}
});

export type OwnProps = {
  baseline?: Partial<FacetData>;
  facetData?: Partial<FacetData>;
};

export function FacetDataContextProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const priceDropDisplayName = usePriceDrop().displayText;

  const baselineFromProps = useMemo(
    () => setPriceDropDisplayName(props.baseline ?? {}, priceDropDisplayName),
    [props.baseline, priceDropDisplayName]
  );
  const facetDataFromProps = useMemo(
    () =>
      props.facetData
        ? setPriceDropDisplayName(props.facetData ?? {}, priceDropDisplayName)
        : undefined,
    [priceDropDisplayName, props.facetData]
  );

  const [baseline, setBaseline] = useState<Partial<FacetData>>(
    setPriceDropDisplayName(baselineFromProps, priceDropDisplayName)
  );
  const facetData = useLoadableState<Partial<FacetData>>(
    useMemo<Partial<FacetData>>(
      () =>
        facetDataFromProps
          ? mergeFromBaseline(facetDataFromProps, baseline)
          : baselineFromProps,
      [baseline, baselineFromProps, facetDataFromProps]
    )
  );
  const [didExpandMenu, setDidExpandMenu] = useState(false);

  useEffect(() => {
    facetData.setData.call(
      null,
      facetDataFromProps
        ? mergeFromBaseline(facetDataFromProps, baselineFromProps)
        : baselineFromProps
    );
  }, [baselineFromProps, facetData.setData, facetDataFromProps]);

  useEffect(() => {
    setBaseline(prevState =>
      setPriceDropDisplayName(prevState, priceDropDisplayName)
    );
    facetData.setData.call(null, prevState =>
      setPriceDropDisplayName(prevState, priceDropDisplayName)
    );
  }, [facetData.setData, priceDropDisplayName]);

  const setFacetDataWithMerge = useCallback<
    React.Dispatch<React.SetStateAction<Partial<FacetData>>>
  >(
    newFacetData => {
      facetData.setData.call(null, prevState =>
        mergeFromBaseline(
          setPriceDropDisplayName(
            typeof newFacetData === 'function'
              ? newFacetData(prevState)
              : newFacetData,
            priceDropDisplayName
          ),
          baseline
        )
      );
    },
    [baseline, facetData.setData, priceDropDisplayName]
  );

  const triggerMenuDidExpand = useCallback(() => {
    setDidExpandMenu(true);
  }, []);

  const value = useMemo<FacetDataContextType>(
    () => ({
      baseline,
      didExpandMenu,
      facetData: {
        ...facetData,
        setData: setFacetDataWithMerge
      },
      setFacetData: setFacetDataWithMerge,
      triggerMenuDidExpand
    }),
    [
      baseline,
      didExpandMenu,
      facetData,
      setFacetDataWithMerge,
      triggerMenuDidExpand
    ]
  );

  return (
    <FacetDataContext.Provider value={value}>
      {props.children}
    </FacetDataContext.Provider>
  );
}

FacetDataContextProvider.displayName = 'FacetDataContextProvider';

export function useFacetData() {
  return useContext(FacetDataContext).facetData.data;
}

export default FacetDataContext;
